/**
 * WorkshopsManager — super-admin CRUD for Quarterly Team Workshops.
 * Full-screen overlay opened from the Dashboard super-admin menu
 * (same pattern as EmailFlowsManager / AnnouncementsManager).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { ArrowLeft, GraduationCap, Plus, Pencil, Trash2, Star, Play } from 'lucide-react';
import { useDesign } from '@/contexts/DesignContext';
import StudioToolHeader from '@/components/studio/admin/StudioToolHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FORM = {
  title: '', description: '', youtube_url: '', quarter_label: '',
  questionsText: '', is_published: true, featured_override: false,
};

const parseYoutubeId = (url) => {
  const m = (url || '').match(/(?:v=|\/embed\/|\/shorts\/|\/live\/|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
};

export default function WorkshopsManager({ userEmail, onClose }) {
  const { isStudio } = useDesign();
  const [workshops, setWorkshops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | workshop id
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/training/admin/workshops?requester_email=${encodeURIComponent(userEmail)}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to load');
      setWorkshops(data.workshops || []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY_FORM); setEditing('new'); };
  const openEdit = (w) => {
    setForm({
      title: w.title, description: w.description || '', youtube_url: w.youtube_url,
      quarter_label: w.quarter_label || '', questionsText: (w.questions || []).join('\n'),
      is_published: w.is_published, featured_override: w.featured_override,
    });
    setEditing(w.id);
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error('Title is required');
    if (!parseYoutubeId(form.youtube_url)) return toast.error('Paste a valid YouTube link');
    setSaving(true);
    try {
      const payload = {
        requester_email: userEmail,
        title: form.title,
        description: form.description,
        youtube_url: form.youtube_url,
        quarter_label: form.quarter_label,
        questions: form.questionsText.split('\n').map((q) => q.trim()).filter(Boolean),
        is_published: form.is_published,
        featured_override: form.featured_override,
      };
      const isNew = editing === 'new';
      const res = await fetch(
        `${API_URL}/api/training/admin/workshops${isNew ? '' : `/${editing}`}`,
        { method: isNew ? 'POST' : 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Save failed');
      toast.success(isNew ? 'Workshop created' : 'Workshop updated');
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (w) => {
    if (!window.confirm(`Delete "${w.title}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(
        `${API_URL}/api/training/admin/workshops/${w.id}?requester_email=${encodeURIComponent(userEmail)}`,
        { method: 'DELETE' },
      );
      if (!res.ok) throw new Error((await res.json()).detail || 'Delete failed');
      toast.success('Workshop deleted');
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const previewId = parseYoutubeId(form.youtube_url);

  return (
    <div className={isStudio ? 'st31-page' : 'min-h-screen bg-gradient-to-br from-slate-50 dark:from-[#1e1f22] to-slate-100 dark:to-[#2d2f33] p-6'} data-testid="workshops-manager">
      {isStudio && (
        <StudioToolHeader
          icon={GraduationCap}
          title="Quarterly workshops"
          sub="One video and its discussion questions, for every church."
          onBack={onClose}
          backTestid="workshops-manager-back"
          right={editing === null ? (
            <button type="button" className="st-btn st-pri st-sm" onClick={openNew} data-testid="workshop-new-btn">
              <Plus /> New workshop
            </button>
          ) : null}
        />
      )}
      <div className={isStudio ? 'st31-pg st31-pg-960' : 'max-w-4xl mx-auto'}>
        {!isStudio && (
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onClose} className="gap-2" data-testid="workshops-manager-back">
              <ArrowLeft className="w-4 h-4" /> Back
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Quarterly Workshops</h1>
            </div>
          </div>
          {editing === null && (
            <Button onClick={openNew} className="gap-2 bg-rose-600 hover:bg-rose-700 text-white" data-testid="workshop-new-btn">
              <Plus className="w-4 h-4" /> New Workshop
            </Button>
          )}
        </div>
        )}

        {editing !== null ? (
          <div className={isStudio ? 'st31-card' : 'bg-white dark:bg-[#2d2f33] rounded-2xl border border-slate-200 dark:border-white/10 p-6 space-y-4'} data-testid="workshop-form">
            {isStudio && <div className="st31-ch"><h2 className="st31-ct">{editing === 'new' ? 'New workshop' : 'Edit workshop'}</h2></div>}
            <div className={isStudio ? 'st31-cb st31-form' : undefined}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="mb-1.5 block">Title *</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Leading Volunteers Who Stay" data-testid="workshop-title-input" />
              </div>
              <div>
                <Label className="mb-1.5 block">Quarter label</Label>
                <Input value={form.quarter_label} onChange={(e) => setForm({ ...form, quarter_label: e.target.value })} placeholder="Q3 2026" data-testid="workshop-quarter-input" />
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block">Description</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="One or two sentences shown on the card." data-testid="workshop-description-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">YouTube link *</Label>
              <Input value={form.youtube_url} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })} placeholder="https://www.youtube.com/watch?v=..." data-testid="workshop-youtube-input" />
              {previewId && (isStudio ? (
                <span className="st31-thumb" style={{ width: 128, height: 72, marginTop: 8 }} data-testid="workshop-thumb-preview">
                  <img src={`https://img.youtube.com/vi/${previewId}/hqdefault.jpg`} alt="Video thumbnail preview" />
                </span>
              ) : (
                <img src={`https://img.youtube.com/vi/${previewId}/hqdefault.jpg`} alt="Video thumbnail preview" className="mt-2 w-40 rounded-lg border border-slate-200 dark:border-white/10" data-testid="workshop-thumb-preview" />
              ))}
            </div>
            <div>
              <Label className="mb-1.5 block">Discussion questions (one per line)</Label>
              <Textarea rows={5} value={form.questionsText} onChange={(e) => setForm({ ...form, questionsText: e.target.value })} placeholder={'What stood out to you most?\nWhere do we see this in our team today?'} data-testid="workshop-questions-input" />
            </div>
            {isStudio ? (<>
              <div className="st31-swrow">
                <div>
                  <div className="st31-nm">Published</div>
                  <div className="st31-mt">Churches can open it as soon as it is published.</div>
                </div>
                <Switch checked={form.is_published} onCheckedChange={(v) => setForm({ ...form, is_published: v })} data-testid="workshop-published-toggle" />
              </div>
              <div className="st31-swrow">
                <div>
                  <div className="st31-nm">Force featured</div>
                  <div className="st31-mt">Otherwise the newest published workshop is featured.</div>
                </div>
                <Switch checked={form.featured_override} onCheckedChange={(v) => setForm({ ...form, featured_override: v })} data-testid="workshop-featured-toggle" />
              </div>
            </>) : (
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch checked={form.is_published} onCheckedChange={(v) => setForm({ ...form, is_published: v })} data-testid="workshop-published-toggle" />
                <Label>Published</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.featured_override} onCheckedChange={(v) => setForm({ ...form, featured_override: v })} data-testid="workshop-featured-toggle" />
                <Label>Force featured <span className="text-xs text-slate-400">(otherwise newest published is featured)</span></Label>
              </div>
            </div>
            )}
            {isStudio ? (
              <div className="st31-cf" style={{ padding: 0, paddingTop: 4 }}>
                <span className="st31-fsp" />
                <button type="button" className="st-btn st-ghost st-sm" onClick={() => setEditing(null)} data-testid="workshop-cancel-btn">Cancel</button>
                <button type="button" className="st-btn st-pri st-sm" onClick={save} disabled={saving} data-testid="workshop-save-btn">
                  {saving ? 'Saving…' : editing === 'new' ? 'Create workshop' : 'Save changes'}
                </button>
              </div>
            ) : (
            <div className="flex gap-3 pt-2">
              <Button onClick={save} disabled={saving} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="workshop-save-btn">
                {saving ? 'Saving…' : editing === 'new' ? 'Create Workshop' : 'Save Changes'}
              </Button>
              <Button variant="ghost" onClick={() => setEditing(null)} data-testid="workshop-cancel-btn">Cancel</Button>
            </div>
            )}
            </div>
          </div>
        ) : loading ? (isStudio ? (
          <div className="st31-card">
            {[0, 1, 2].map(i => (
              <div key={i} className="st31-wrow">
                <span className="st31-sk st31-thumb" style={{ background: undefined }} />
                <span className="st31-c st31-skstat">
                  <span className="st31-sk" style={{ width: '42%', height: 14 }} />
                  <span className="st31-sk" style={{ width: '64%', height: 12 }} />
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 text-slate-400">Loading…</div>
        )) : workshops.length === 0 ? (isStudio ? (
          <div className="st31-card">
            <div className="st-empty" style={{ padding: '44px 24px' }} data-testid="workshops-empty">
              <span className="st-glyph"><GraduationCap /></span>
              <b>No workshops yet</b>
              <span>Create the first one and every church sees it this quarter.</span>
              <button type="button" className="st-btn st-sec st-sm" style={{ marginTop: 10 }} onClick={openNew}><Plus /> New workshop</button>
            </div>
          </div>
        ) : (
          <div className="text-center py-16 text-slate-400" data-testid="workshops-empty">No workshops yet. Create the first one!</div>
        )) : (
          <div className={isStudio ? 'st31-card' : 'space-y-3'} data-testid="workshops-list">
            {workshops.map((w, idx) => {
              const isFeatured = w.featured_override || (w.is_published && idx === workshops.findIndex((x) => x.is_published) && !workshops.some((x) => x.featured_override));
              return (
                isStudio ? (
                <div key={w.id} className="st31-wrow" data-testid={`workshop-row-${w.id}`}>
                  <span className="st31-thumb">{w.thumbnail_url ? <img src={w.thumbnail_url} alt="" /> : <Play />}</span>
                  <span className="st31-c">
                    <span className="st31-t">
                      {w.title}
                      {w.quarter_label && <span className="st-chip st-tb-xs">{w.quarter_label}</span>}
                      {!w.is_published && <span className="st-chip st-tb-xs">Draft</span>}
                      {isFeatured && <span className="st-chip st-tb-xs"><Star /> Featured</span>}
                    </span>
                    <span className="st31-m">{(w.questions || []).length} discussion questions{w.description ? ` · ${w.description}` : ''}</span>
                  </span>
                  <button type="button" className="st-ibtn" style={{ width: 30, height: 30 }} onClick={() => openEdit(w)} data-testid={`workshop-edit-${w.id}`} aria-label="Edit workshop"><Pencil /></button>
                  <button type="button" className="st-ibtn" style={{ width: 30, height: 30 }} onClick={() => remove(w)} data-testid={`workshop-delete-${w.id}`} aria-label="Delete workshop"><Trash2 /></button>
                </div>
                ) : (
                <div key={w.id} className="bg-white dark:bg-[#2d2f33] rounded-xl border border-slate-200 dark:border-white/10 p-4 flex items-center gap-4" data-testid={`workshop-row-${w.id}`}>
                  <img src={w.thumbnail_url} alt="" className="w-24 h-14 rounded-lg object-cover flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-800 dark:text-slate-100 truncate">{w.title}</span>
                      {w.quarter_label && <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 dark:bg-rose-500/20 text-rose-600 dark:text-rose-300 font-semibold">{w.quarter_label}</span>}
                      {!w.is_published && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-white/10 text-slate-500 font-semibold">DRAFT</span>}
                      {isFeatured && <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-300 font-semibold"><Star className="w-3 h-3" /> FEATURED</span>}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5">{(w.questions || []).length} discussion questions{w.description ? ` · ${w.description}` : ''}</div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(w)} data-testid={`workshop-edit-${w.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(w)} className="text-red-500 hover:text-red-600" data-testid={`workshop-delete-${w.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
                )
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
